import React, { useState, useEffect } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap, Polyline } from "react-leaflet";
import L from "leaflet";
import AddressFetcher from "Components/Photon/AddressFetcher";
import { AutoComplete } from 'primereact/autocomplete';
import axios from 'axios';
import { classNames } from "primereact/utils";
import Translate from "Components/Translate";
import { Button } from "primereact/button";

// Custom marker icons with labels
const createCustomIcon = (color, label) => {
  return L.divIcon({
    className: 'custom-div-icon',
    html: `
      <div style="position: relative;">
        <img src="https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-${color}.png" 
             style="width: 25px; height: 41px;"/>
        <div style="
          position: absolute;
          width: max-content;
          top: -30px;
          left: 50%;
          transform: translateX(-50%);
          background: white;
          padding: 2px 8px;
          border-radius: 4px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.2);
          font-weight: bold;
          font-size: 12px;
        ">${label}</div>
      </div>
    `,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
  });
};

const departureIcon = createCustomIcon('green', 'Départ');
const arrivalIcon = createCustomIcon('red', 'Arrivée');

// Component to recenter and fit bounds
const MapController = ({ departure, arrival }) => {
  const map = useMap();

  useEffect(() => {
    if (departure && arrival) {
      const bounds = L.latLngBounds([
        [departure.lat, departure.lng],
        [arrival.lat, arrival.lng]
      ]);
      map.fitBounds(bounds, { padding: [50, 50] });
    } else if (departure) {
      map.setView([departure.lat, departure.lng], 15);
    } else if (arrival) {
      map.setView([arrival.lat, arrival.lng], 15);
    }
  }, [departure, arrival]);

  return null;
};

const SelectMap = ({ inputs, setInputs, onValidSubmit }) => {
  const [departure, setDeparture] = useState(null);
  const [arrival, setArrival] = useState(null);
  const [departureSearch, setDepartureSearch] = useState('');
  const [arrivalSearch, setArrivalSearch] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [activeMarker, setActiveMarker] = useState('departure');
  const [errors, setErrors] = useState({});
  const [routePoints, setRoutePoints] = useState([]);

  let initMapPosition = {
    lat: JSON.parse(process.env.REACT_APP_INIT_MAP_POSITION)[0],
    lng: JSON.parse(process.env.REACT_APP_INIT_MAP_POSITION)[1]
  }
  
  let country_name = process.env.REACT_APP_COUNTRY_NAME;
  let country_code = process.env.REACT_APP_COUNTRY_CODE;
  // Update route points when markers change
  useEffect(() => {
    if (departure?.lat && departure?.lng && arrival?.lat && arrival?.lng) {
      fetchRoute();
    }
  }, [departure, arrival]);

  // Expose validation method to parent
  useEffect(() => {
    if (onValidSubmit) {
      onValidSubmit({
        validateLocations: () => {
          const newErrors = {};
          
          if (!departure?.lat || !departure?.lng) {
            newErrors.departure = "Le point de départ est requis";
          }
          
          if (!arrival?.lat || !arrival?.lng) {
            newErrors.arrival = "Le point d'arrivée est requis";
          }

          setErrors(newErrors);
          return Object.keys(newErrors).length === 0;
        }
      });
    }
  }, [departure, arrival, onValidSubmit]);

  const fetchRoute = async () => {
    try {
      const response = await axios.get(
        `https://router.project-osrm.org/route/v1/driving/${departure.lng},${departure.lat};${arrival.lng},${arrival.lat}?overview=full&geometries=geojson`
      );
      
      if (response.data.routes && response.data.routes[0]) {
        const coordinates = response.data.routes[0].geometry.coordinates.map(coord => [coord[1], coord[0]]);
        setRoutePoints(coordinates);
      }
    } catch (error) {
      console.error('Error fetching route:', error);
      // Fallback to straight line if route fetching fails
      setRoutePoints([
        [departure.lat, departure.lng],
        [arrival.lat, arrival.lng]
      ]);
    }
  };

  const onChangeDeparture = (value) => {
    setInputs((prevState) => ({
      ...prevState,
      departure: value,
    }));
    setErrors((prev) => ({ ...prev, departure: null }));
  };

  const onChangeArrival = (value) => {
    setInputs((prevState) => ({
      ...prevState,
      arrival: value,
    }));
    setErrors((prev) => ({ ...prev, arrival: null }));
  };

  const updateLocation = (type, lat, lng) => {
    const newLocation = { lat, lng };
    if (type === "departure") {
      setDeparture(newLocation);
      setActiveMarker("departure");
      setErrors((prev) => ({ ...prev, departure: null }));
    } else {
      setArrival(newLocation);
      setActiveMarker("arrival");
      setErrors((prev) => ({ ...prev, arrival: null }));
    }
  };

  const searchAddress = async (event, type) => {
    const query = event.query.trim();
    
    // Only search if query is longer than 4 characters
    if (query.length < 4) {
      setSuggestions([]);
      return;
    }

    try {
      // Add Tunisia to the search query to improve results
      const searchQuery = `${query} ${country_name}`;
      const response = await axios.get(
        `https://photon.taxiora.net/api/?q=${searchQuery}&limit=10&lat=${initMapPosition.lat}&lon=${initMapPosition.lng}&lang=fr`
      );
      
      const suggestions = response.data.features
        // Filter to ensure only Tunisia results
        .filter(feature => 
          feature.properties.country === country_name || 
          feature.properties.countrycode === country_code
        )
        .map(feature => {
          // Extract coordinates in the correct order
          const [longitude, latitude] = feature.geometry.coordinates;
          
          // Build the address label
          const addressParts = [];
          if (feature.properties.name) addressParts.push(feature.properties.name);
          if (feature.properties.street && feature.properties.street !== feature.properties.name) 
            addressParts.push(feature.properties.street);
          if (feature.properties.district) addressParts.push(feature.properties.district);
          if (feature.properties.city) addressParts.push(feature.properties.city);
          if (feature.properties.state && feature.properties.state !== feature.properties.city)
            addressParts.push(feature.properties.state);
          
          return {
            label: addressParts.join(', '),
            coordinates: {
              lat: latitude,
              lng: longitude
            }
          };
        })
        .slice(0, 5); // Limit to 5 results after filtering
      
      setSuggestions(suggestions);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setSuggestions([]);
    }
  };

  const onSuggestionSelect = (e, type) => {
    if (!e?.coordinates?.lat || !e?.coordinates?.lng) {
      console.error('Invalid coordinates in selection:', e);
      return;
    }

    const { lat, lng } = e.coordinates;
    updateLocation(type, lat, lng);
    
    if (type === 'departure') {
      setDepartureSearch(e.label);
    } else {
      setArrivalSearch(e.label);
    }
  };

  const LocationHandler = () => {
    useMapEvents({
      click(e) {
        if (activeMarker === 'arrival') {
          updateLocation("arrival", e?.latlng?.lat, e?.latlng?.lng);
        } else {
          updateLocation("departure", e?.latlng?.lat, e?.latlng?.lng);
        }
      }
    });

    return (
      <>
        {routePoints.length > 0 && (
          <Polyline
            positions={routePoints}
            color="#2196F3"
            weight={4}
            opacity={0.8}
            dashArray="10, 10"
          />
        )}
        {departure && departure.lat && departure.lng && (
          <Marker
            position={[departure.lat, departure.lng]}
            icon={departureIcon}
            draggable
            eventHandlers={{
              dragend: (e) => {
                const { lat, lng } = e.target.getLatLng();
                updateLocation("departure", lat, lng);
              },
              click: () => setActiveMarker("departure")
            }}
          />
        )}
        {arrival && arrival.lat && arrival.lng && (
          <Marker
            position={[arrival.lat, arrival.lng]}
            icon={arrivalIcon}
            draggable
            eventHandlers={{
              dragend: (e) => {
                const { lat, lng } = e.target.getLatLng();
                updateLocation("arrival", lat, lng);
              },
              click: () => setActiveMarker("arrival")
            }}
          />
        )}
      </>
    );
  };

  return (
    <div className="card p-4">
      <div className="grid">
        <div className="col-12 md:col-6">
          <div className="field">
            <label className={classNames({ "p-error": errors.departure })}>
              <i className="pi pi-map-marker text-green-500 mr-2" />
              <Translate>Point de départ</Translate>*
            </label>
            <AutoComplete
              value={departureSearch}
              suggestions={suggestions}
              completeMethod={(e) => searchAddress(e, 'departure')}
              field="label"
              dropdown
              forceSelection
              onChange={(e) => setDepartureSearch(e.value)}
              onSelect={(e) => onSuggestionSelect(e.value, 'departure')}
              className={classNames({ "w-full": true, "p-invalid": errors.departure })}
              placeholder="Rechercher une adresse (min. 4 caractères)..."
              minLength={4}
            />
            {errors.departure && (
              <small className="p-error block mt-1">{errors.departure}</small>
            )}
            {departure && (
              <AddressFetcher
                lat={departure?.lat}
                lon={departure?.lng}
                onChange={onChangeDeparture}
              >
                {(address) => (
                  <small className="text-500 block mt-1">
                    <i className="pi pi-info-circle mr-1" />
                    {address}
                  </small>
                )}
              </AddressFetcher>
            )}
          </div>
        </div>

        <div className="col-12 md:col-6">
          <div className="field">
            <label className={classNames({ "p-error": errors.arrival })}>
              <i className="pi pi-map-marker text-red-500 mr-2" />
              <Translate>Point d'arrivée</Translate>*
            </label>
            <AutoComplete
              value={arrivalSearch}
              suggestions={suggestions}
              completeMethod={(e) => searchAddress(e, 'arrival')}
              field="label"
              dropdown
              forceSelection
              onChange={(e) => setArrivalSearch(e.value)}
              onSelect={(e) => onSuggestionSelect(e.value, 'arrival')}
              className={classNames({ "w-full": true, "p-invalid": errors.arrival })}
              placeholder="Rechercher une adresse (min. 4 caractères)..."
              minLength={4}
            />
            {errors.arrival && (
              <small className="p-error block mt-1">{errors.arrival}</small>
            )}
            {arrival && (
              <AddressFetcher
                lat={arrival?.lat}
                lon={arrival?.lng}
                onChange={onChangeArrival}
              >
                {(address) => (
                  <small className="text-500 block mt-1">
                    <i className="pi pi-info-circle mr-1" />
                    {address}
                  </small>
                )}
              </AddressFetcher>
            )}
          </div>
        </div>
      </div>

      <div className="flex align-items-center gap-2 mb-3">
        <Button
          icon="pi pi-map-marker"
          onClick={() => setActiveMarker('departure')}
          severity={activeMarker === 'departure' ? "success" : "secondary"}
          className="p-button-rounded"
          label="Point de départ"
        />
        <Button
          icon="pi pi-map-marker"
          onClick={() => setActiveMarker('arrival')}
          severity={activeMarker === 'arrival' ? "danger" : "secondary"}
          className="p-button-rounded"
          label="Point d'arrivée"
        />
      </div>

      <div className="card">
        <MapContainer
          center={[initMapPosition.lat, initMapPosition.lng]}
          zoom={13}
          style={{ height: "500px", width: "100%", borderRadius: "8px" }}
        >
          <TileLayer url="https://maps.taxiora.net/tile/{z}/{x}/{y}.png"  />
          <LocationHandler />
          <MapController departure={departure} arrival={arrival} />
        </MapContainer>
      </div>

      <small className="text-500 block mt-2">
        <i className="pi pi-info-circle mr-1" />
        <em>
          Sélectionnez le type de point (départ/arrivée) puis cliquez sur la carte pour placer le marqueur
        </em>
      </small>
    </div>
  );
};

export default SelectMap;
